/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {PlatformLocation} from '@angular/common';
import {platformCoreDynamic} from '@angular/compiler';
import {Injectable, NgModule, PLATFORM_INITIALIZER, PlatformRef, Provider, RootRenderer, createPlatformFactory, isDevMode, platformCore} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {Parse5DomAdapter} from './parse5_adapter';
import {DebugDomRootRenderer} from './private_import_core';
import {SharedStylesHost} from './private_import_platform-browser';
import {ServerRootRenderer} from './server_renderer';
/**
 * @param {?} feature
 * @return {?}
 */
function notSupported(feature: string): Error {
  throw new Error(`platform-server does not support '${feature}'.`);
}

class ServerPlatformLocation extends PlatformLocation {
/**
 * @return {?}
 */
getBaseHrefFromDOM(): string { throw notSupported('getBaseHrefFromDOM'); };
/**
 * @param {?} fn
 * @return {?}
 */
onPopState(fn: any): void { notSupported('onPopState'); };
/**
 * @param {?} fn
 * @return {?}
 */
onHashChange(fn: any): void { notSupported('onHashChange'); };
/**
 * @return {?}
 */
get pathname(): string { throw notSupported('pathname'); }
/**
 * @return {?}
 */
get search(): string { throw notSupported('search'); }
/**
 * @return {?}
 */
get hash(): string { throw notSupported('hash'); }
/**
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @return {?}
 */
replaceState(state: any, title: string, url: string): void { notSupported('replaceState'); };
/**
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @return {?}
 */
pushState(state: any, title: string, url: string): void { notSupported('pushState'); };
/**
 * @return {?}
 */
forward(): void { notSupported('forward'); };
/**
 * @return {?}
 */
back(): void { notSupported('back'); };
}

export const /** @type {?} */ INTERNAL_SERVER_PLATFORM_PROVIDERS: Array<any /*Type | Provider | any[]*/> = [
  {provide: PLATFORM_INITIALIZER, useValue: initParse5Adapter, multi: true},
  {provide: PlatformLocation, useClass: ServerPlatformLocation},
];
/**
 * @return {?}
 */
function initParse5Adapter() {
  Parse5DomAdapter.makeCurrent();
}
/**
 * @param {?} rootRenderer
 * @return {?}
 */
export function _createConditionalRootRenderer(rootRenderer: any) {
  if (isDevMode()) {
    return new DebugDomRootRenderer(rootRenderer);
  }
  return rootRenderer;
}

export const /** @type {?} */ SERVER_RENDER_PROVIDERS: Provider[] = [
  ServerRootRenderer,
  {provide: RootRenderer, useFactory: _createConditionalRootRenderer, deps: [ServerRootRenderer]},
  // use plain SharedStylesHost, not the DomSharedStylesHost
  SharedStylesHost
];
/**
 *  The ng module for the server.
  * *
 */
export class ServerModule {
static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{exports: [BrowserModule], providers: SERVER_RENDER_PROVIDERS}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function ServerModule_tsickle_Closure_declarations() {
 /** @type {?} */
ServerModule.decorators;
 /**
  * @nocollapse
  * @type {?}
  */
ServerModule.ctorParameters;
}


/**
 * @experimental
 */
export const /** @type {?} */ platformServer =
    createPlatformFactory(platformCore, 'server', INTERNAL_SERVER_PLATFORM_PROVIDERS);

/**
 * The server platform that supports the runtime compiler.
 *
 * @experimental
 */
export const /** @type {?} */ platformDynamicServer =
    createPlatformFactory(platformCoreDynamic, 'serverDynamic', INTERNAL_SERVER_PLATFORM_PROVIDERS);

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
